import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  Divider,
  TextField,
  Autocomplete,
  Paper,
  Grid,
  FormControlLabel,
  Switch,
} from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AddLinkIcon from '@mui/icons-material/AddLink';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import walletApi, { getCorrelations, deleteCorrelation, createCorrelation } from '../../api/wallet';
import { formatValue, toDisplayDate } from '../../utils/utils';
import { getErrorMessage } from '../../utils/errors';
import CurrencyInput from '../commons/CurrencyInput';

const CorrelatedExpensesList = ({
  parentId,
  onAddCorrelation,
  onCorrelationChange,
  categories,
  parentCurrency = 'BRL',
  parentDebitDate,
  parentDescription,
}) => {
  const [loading, setLoading] = useState(true);
  const [correlations, setCorrelations] = useState([]);
  const [parent, setParent] = useState(null);
  const [totalCorrelatedValue, setTotalCorrelatedValue] = useState('0');
  const [error, setError] = useState('');
  const [unlinking, setUnlinking] = useState(null);
  const [updatingMandatory, setUpdatingMandatory] = useState(null);

  // New expense form state
  const [showNewForm, setShowNewForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newExpense, setNewExpense] = useState({
    description: '',
    category: null,
    value: 0,
    isMandatory: false,
  });

  const resetNewForm = () => {
    setNewExpense({ description: '', category: null, value: 0, isMandatory: false });
    setShowNewForm(false);
  };

  const handleCreateNew = async () => {
    if (!newExpense.description || !newExpense.category || !newExpense.value) {
      setError('Preencha todos os campos');
      return;
    }

    setCreating(true);
    setError('');

    try {
      // Create the child expense
      const debitDateFormatted = parentDebitDate
        ? toDisplayDate(parentDebitDate)
        : new Date().toLocaleDateString('pt-BR');

      const response = await walletApi.post('/cashout', {
        Description: newExpense.description,
        CategoryId: newExpense.category.ID,
        IsMandatory: newExpense.isMandatory,
        Value: String(newExpense.value),
        DebitDate: debitDateFormatted,
        Currency: parentCurrency,
        Observation: `Parte de: ${parentDescription || ''}`,
      });

      const childId = response.data?.ID;

      // Link to parent
      if (childId) {
        await createCorrelation(parentId, childId);
        resetNewForm();
        // Update correlations list and total locally, then notify parent
        const newCorrelation = {
          id: childId,
          description: newExpense.description,
          category_id: newExpense.category.ID,
          category_name: newExpense.category.Name,
          value: String(newExpense.value),
          currency: parentCurrency,
          debit_date: parentDebitDate ? toDisplayDate(parentDebitDate) : new Date().toLocaleDateString('pt-BR'),
          is_mandatory: newExpense.isMandatory,
          observation: `Parte de: ${parentDescription || ''}`,
        };
        setCorrelations((prev) => [...prev, newCorrelation]);
        setTotalCorrelatedValue((prev) => String(Number(prev) + Number(newExpense.value)));
        onCorrelationChange?.(true);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao criar despesa'));
    } finally {
      setCreating(false);
    }
  };

  const fetchCorrelations = useCallback(async () => {
    if (!parentId) return;

    setLoading(true);
    setError('');

    try {
      const response = await getCorrelations(parentId);
      const data = response.data;
      setParent(data.parent);
      setCorrelations(data.correlations || []);
      setTotalCorrelatedValue(data.total_correlated_value || '0');
    } catch (err) {
      if (err.response?.status !== 404) {
        setError(getErrorMessage(err, 'Erro ao carregar correlacoes'));
      }
    } finally {
      setLoading(false);
    }
  }, [parentId]);

  useEffect(() => {
    fetchCorrelations();
  }, [fetchCorrelations]);

  const handleUnlink = async (childId) => {
    setUnlinking(childId);
    setError('');

    try {
      await deleteCorrelation(parentId, childId);
      // Update state optimistically
      const removedCorrelation = correlations.find((c) => c.id === childId);
      const newCorrelations = correlations.filter((c) => c.id !== childId);
      setCorrelations(newCorrelations);
      if (removedCorrelation) {
        setTotalCorrelatedValue((prev) => String(Math.max(0, Number(prev) - Number(removedCorrelation.value || 0))));
      }
      onCorrelationChange?.(newCorrelations.length > 0);
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao remover correlacao'));
    } finally {
      setUnlinking(null);
    }
  };

  const handleToggleMandatory = async (expense) => {
    setUpdatingMandatory(expense.id);
    setError('');

    try {
      const newMandatory = !expense.is_mandatory;
      // Convert date format if needed (API returns YYYY-MM-DD, but update expects DD/MM/YYYY)
      let formattedDate = expense.debit_date;
      if (expense.debit_date && /^\d{4}-\d{2}-\d{2}$/.test(expense.debit_date)) {
        formattedDate = toDisplayDate(expense.debit_date);
      }
      await walletApi.put('/cashout', {
        id: expense.id,
        description: expense.description,
        category_id: expense.category_id,
        observation: expense.observation || '',
        is_mandatory: newMandatory,
        value: String(expense.value),
        debit_date: formattedDate,
      });
      // Update state optimistically
      setCorrelations((prev) =>
        prev.map((c) =>
          c.id === expense.id ? { ...c, is_mandatory: newMandatory } : c
        )
      );
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao atualizar'));
    } finally {
      setUpdatingMandatory(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Despesas Correlacionadas ({correlations.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowNewForm(true)}
            sx={{ textTransform: 'none' }}
            disabled={showNewForm}
          >
            Criar nova
          </Button>
          <Button
            size="small"
            startIcon={<AddLinkIcon />}
            onClick={onAddCorrelation}
            sx={{ textTransform: 'none' }}
            variant="outlined"
          >
            Vincular existente
          </Button>
        </Box>
      </Box>

      {showNewForm && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'background.default' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2">Nova despesa vinculada</Typography>
            <IconButton size="small" onClick={resetNewForm}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <TextField
            label="Descrição"
            variant="outlined"
            size="small"
            fullWidth
            value={newExpense.description}
            onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
            sx={{ mb: 1.5 }}
          />

          <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
            <Grid item xs={7}>
              <Autocomplete
                options={categories || []}
                getOptionLabel={(option) => option.Name || ''}
                value={newExpense.category}
                onChange={(e, val) => setNewExpense({ ...newExpense, category: val })}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} label="Categoria" variant="outlined" />
                )}
              />
            </Grid>
            <Grid item xs={5}>
              <CurrencyInput
                label="Valor"
                variant="outlined"
                size="small"
                fullWidth
                value={newExpense.value}
                onChange={(val) => setNewExpense({ ...newExpense, value: val })}
                currency={parentCurrency}
              />
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Switch
                checked={newExpense.isMandatory}
                onChange={(e) => setNewExpense({ ...newExpense, isMandatory: e.target.checked })}
                size="small"
              />
            }
            label="Obrigatório"
            sx={{ mb: 1 }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={handleCreateNew}
            disabled={creating || !newExpense.description || !newExpense.category || !newExpense.value}
            fullWidth
          >
            {creating ? 'Criando...' : 'Criar e vincular'}
          </Button>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {correlations.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Nenhuma despesa correlacionada
        </Typography>
      ) : (
        <>
          <List dense disablePadding>
            {correlations.map((expense) => (
              <ListItem
                key={expense.id}
                disablePadding
                sx={{
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={expense.is_mandatory ? 'Despesa fixa' : 'Despesa variável'}>
                      <Switch
                        size="small"
                        checked={expense.is_mandatory || false}
                        onChange={() => handleToggleMandatory(expense)}
                        disabled={updatingMandatory === expense.id}
                      />
                    </Tooltip>
                    <Typography variant="body2" color="error.main" fontWeight={500} sx={{ minWidth: 70, textAlign: 'right' }}>
                      {formatValue(expense.value, expense.currency)}
                    </Typography>
                    <Tooltip title="Remover correlacao">
                      <IconButton
                        size="small"
                        onClick={() => handleUnlink(expense.id)}
                        disabled={unlinking === expense.id}
                      >
                        {unlinking === expense.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <LinkOffIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <ListItemText
                  primary={expense.description}
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={expense.category_name}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.7rem' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {expense.debit_date}
                      </Typography>
                    </Box>
                  }
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Total correlacionado:
            </Typography>
            <Typography variant="body2" fontWeight={600} color="info.main">
              {formatValue(totalCorrelatedValue, 'BRL')}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CorrelatedExpensesList;
